import React, { useState, useEffect } from "react";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";

interface BugReport {
  id: string;
  reporter_user_id: string;
  reporter_username: string;
  reporter_avatar: string | null;
  title: string;
  description: string;
  steps_to_reproduce: string | null;
  expected_behavior: string | null;
  actual_behavior: string | null;
  url: string | null;
  browser: string | null;
  device: string | null;
  screenshot_url: string | null;
  severity: "low" | "medium" | "high" | "critical";
  category: "ui" | "functionality" | "performance" | "data" | "other";
  status: "new" | "investigating" | "in_progress" | "resolved" | "wont_fix" | "duplicate";
  assigned_to_user_id: string | null;
  assigned_to_username: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  comments?: BugComment[];
}

interface BugComment {
  id: string;
  author_user_id: string;
  author_username: string;
  content: string;
  is_internal: boolean;
  created_at: string;
}

type StatusFilter = "all" | BugReport["status"] | "archived";

export default function BugsAdminPage() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [selectedBug, setSelectedBug] = useState<BugReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [newComment, setNewComment] = useState("");
  const [isInternalComment, setIsInternalComment] = useState(false);

  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchBugs();
  }, []);

  // Fetch signed URL when selectedBug changes
useEffect(() => {
  const fetchScreenshotUrl = async () => {
    if (selectedBug?.screenshot_url && selectedBug.screenshot_url.startsWith('private/')) {
      try {
        const response = await fetch(`/bugs/screenshot?key=${encodeURIComponent(selectedBug.screenshot_url)}`, {          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setScreenshotUrl(data.url);
        }
      } catch (error) {
        console.error('Error fetching screenshot URL:', error);
      }
    } else if (selectedBug?.screenshot_url) {
      // Legacy URLs or public URLs - use directly
      setScreenshotUrl(selectedBug.screenshot_url);
    } else {
      setScreenshotUrl(null);
    }
  };
  
  fetchScreenshotUrl();
}, [selectedBug]);  
  const fetchBugs = async () => {
    try {
      const response = await fetch("/bugs/admin/all", {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch bugs");

      const data = await response.json();
      setBugs(data);
    } catch (error) {
      console.error("Error fetching bugs:", error);
      showToast("Failed to load bug reports", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBugDetails = async (bugId: string) => {
    try {
      const response = await fetch(`/bugs/admin/${bugId}`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch bug details");

      const data = await response.json();
      setSelectedBug(data);
    } catch (error) {
      console.error("Error fetching bug details:", error);
      showToast("Failed to load bug details", "error");
    }
  };

  const updateBugStatus = async (bugId: string, status: BugReport["status"]) => {
    try {
      const response = await fetch(`/bugs/admin/${bugId}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error("Failed to update status");

      showToast("Bug status updated", "success");
      await fetchBugs();
      if (selectedBug?.id === bugId) {
        await fetchBugDetails(bugId);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Failed to update status", "error");
    }
  };

  const archiveBug = async (bugId: string) => {
    const confirmed = await confirm({
      title: "Archive this bug report?",
      description: "Archived bugs can be restored later.",
      confirmText: "Archive",
      variant: "info",
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`/bugs/admin/${bugId}/archive`, {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to archive bug");

      showToast("Bug archived", "success");
      await fetchBugs();
      setSelectedBug(null);
    } catch (error) {
      console.error("Error archiving bug:", error);
      showToast("Failed to archive bug", "error");
    }
  };

  const restoreBug = async (bugId: string) => {
    try {
      const response = await fetch(`/bugs/admin/${bugId}/restore`, {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to restore bug");

      showToast("Bug restored", "success");
      await fetchBugs();
      if (selectedBug?.id === bugId) {
        await fetchBugDetails(bugId);
      }
    } catch (error) {
      console.error("Error restoring bug:", error);
      showToast("Failed to restore bug", "error");
    }
  };

  const addComment = async () => {
    if (!selectedBug || !newComment.trim()) return;

    try {
      const response = await fetch(`/bugs/admin/${selectedBug.id}/comment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: newComment,
          is_internal: isInternalComment,
        }),
      });

      if (!response.ok) throw new Error("Failed to add comment");

      showToast("Comment added", "success");
      setNewComment("");
      setIsInternalComment(false);
      await fetchBugDetails(selectedBug.id);
    } catch (error) {
      console.error("Error adding comment:", error);
      showToast("Failed to add comment", "error");
    }
  };

  const filteredBugs = bugs.filter((bug) => {
    // Status filter
    if (statusFilter === "archived" && !bug.is_archived) return false;
    if (statusFilter !== "all" && statusFilter !== "archived" && bug.status !== statusFilter) return false;
    if (statusFilter !== "archived" && bug.is_archived) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        bug.title.toLowerCase().includes(query) ||
        bug.description.toLowerCase().includes(query) ||
        bug.reporter_username.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const statusColors = {
    new: { bg: "bg-cyan-500/20", border: "border-cyan-500/40", text: "text-cyan-800 dark:text-cyan-200" },
    investigating: { bg: "bg-purple-500/20", border: "border-purple-500/40", text: "text-purple-800 dark:text-purple-200" },
    in_progress: { bg: "bg-blue-500/20", border: "border-blue-500/40", text: "text-blue-800 dark:text-blue-200" },
    resolved: { bg: "bg-green-500/20", border: "border-green-500/40", text: "text-green-800 dark:text-green-200" },
    wont_fix: { bg: "bg-gray-500/20", border: "border-gray-500/40", text: "text-[var(--c-ink)]" },
    duplicate: { bg: "bg-yellow-500/20", border: "border-yellow-500/40", text: "text-yellow-800 dark:text-yellow-200" },
  };

  const severityColors = {
    low: { bg: "bg-blue-500/10", text: "text-blue-700 dark:text-blue-300" },
    medium: { bg: "bg-yellow-500/10", text: "text-yellow-700 dark:text-yellow-300" },
    high: { bg: "bg-orange-500/10", text: "text-orange-700 dark:text-orange-300" },
    critical: { bg: "bg-red-500/20", text: "text-red-700 dark:text-red-300" },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[var(--c-ink-2)]">Loading bug reports...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[var(--c-ink)] mb-2 flex items-center gap-3">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Bug Reports Management
        </h1>
        <p className="text-sm text-[var(--c-ink-2)]">
          {filteredBugs.length} bug{filteredBugs.length !== 1 ? "s" : ""} found
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search bugs..."
          className="flex-1 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] px-4 py-2 text-sm outline-none focus:border-red-500/50 transition-colors"
        />
        <div className="flex gap-2 overflow-x-auto">
          {(["all", "new", "investigating", "in_progress", "resolved", "archived"] as StatusFilter[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setStatusFilter(filter)}
              className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                statusFilter === filter
                  ? "bg-red-500/30 border-2 border-red-500/50 text-red-800 dark:text-red-200"
                  : "bg-[var(--c-sunken)] border-2 border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
              }`}
            >
              {filter.replace("_", " ").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-6 min-h-0">
        {/* Bug List */}
        <div className="overflow-y-auto space-y-3 pr-2">
          {filteredBugs.length === 0 ? (
            <div className="text-center py-12 text-[var(--c-ink-3)]">
              No bugs found matching your filters
            </div>
          ) : (
            filteredBugs.map((bug) => (
              <button
                key={bug.id}
                onClick={() => fetchBugDetails(bug.id)}
                className={`w-full text-left rounded-xl border p-4 transition-all hover:border-red-500/30 ${
                  selectedBug?.id === bug.id
                    ? "border-red-500/50 bg-red-500/10"
                    : bug.is_archived
                    ? "border-[var(--c-line-soft)] bg-[var(--c-surface)] opacity-60"
                    : "border-[var(--c-line)] bg-[var(--c-surface)]"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-semibold text-sm text-[var(--c-ink)] line-clamp-1">{bug.title}</h3>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${severityColors[bug.severity].bg} ${severityColors[bug.severity].text}`}>
                    {bug.severity}
                  </span>
                </div>

                <p className="text-xs text-[var(--c-ink-3)] line-clamp-2 mb-3">{bug.description}</p>

                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase border ${statusColors[bug.status].bg} ${statusColors[bug.status].border} ${statusColors[bug.status].text}`}>
                    {bug.status.replace("_", " ")}
                  </span>
                  <div className="flex items-center gap-2 text-[10px] text-[var(--c-ink-3)]">
                    <span>{bug.reporter_username}</span>
                    <span>·</span>
                    <span>{new Date(bug.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Bug Details Panel */}
        <div className="overflow-y-auto rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)]">
          {selectedBug ? (
            <div className="p-6 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-[var(--c-ink)] mb-2">{selectedBug.title}</h2>
                    <div className="flex items-center gap-3 text-xs text-[var(--c-ink-3)]">
                      <span>Reported by {selectedBug.reporter_username}</span>
                      <span>·</span>
                      <span>{new Date(selectedBug.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase ${severityColors[selectedBug.severity].bg} ${severityColors[selectedBug.severity].text}`}>
                      {selectedBug.severity}
                    </span>
                    <span className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase bg-[var(--c-sunken-2)] text-[var(--c-ink-2)]">
                      {selectedBug.category}
                    </span>
                  </div>
                </div>

                {selectedBug.is_archived && (
                  <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-800 dark:text-yellow-200 flex items-center justify-between">
                    <span>⚠️ This bug is archived</span>
                    <button
                      onClick={() => restoreBug(selectedBug.id)}
                      className="px-3 py-1 rounded bg-yellow-500/20 hover:bg-yellow-500/30 text-xs font-bold transition-colors"
                    >
                      Restore
                    </button>
                  </div>
                )}
              </div>

              {/* Status Actions */}
              <div>
                <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-2">Status</label>
                <div className="flex flex-wrap gap-2">
                  {(["new", "investigating", "in_progress", "resolved", "wont_fix", "duplicate"] as BugReport["status"][]).map((status) => (
                    <button
                      key={status}
                      onClick={() => updateBugStatus(selectedBug.id, status)}
                      disabled={selectedBug.is_archived}
                      className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all border-2 ${
                        selectedBug.status === status
                          ? `${statusColors[status].bg} ${statusColors[status].border} ${statusColors[status].text}`
                          : "border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                    >
                      {status.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-2">Description</label>
                <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] p-4 text-sm text-[var(--c-ink)] whitespace-pre-wrap">
                  {selectedBug.description}
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedBug.steps_to_reproduce && (
                  <div>
                    <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-2">Steps to Reproduce</label>
                    <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] p-3 text-xs text-[var(--c-ink-2)] whitespace-pre-wrap">
                      {selectedBug.steps_to_reproduce}
                    </div>
                  </div>
                )}

                {selectedBug.expected_behavior && (
                  <div>
                    <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-2">Expected Behavior</label>
                    <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] p-3 text-xs text-[var(--c-ink-2)] whitespace-pre-wrap">
                      {selectedBug.expected_behavior}
                    </div>
                  </div>
                )}

                {selectedBug.actual_behavior && (
                  <div>
                    <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-2">Actual Behavior</label>
                    <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] p-3 text-xs text-[var(--c-ink-2)] whitespace-pre-wrap">
                      {selectedBug.actual_behavior}
                    </div>
                  </div>
                )}
              </div>

              {/* Technical Info */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                {selectedBug.url && (
                  <div>
                    <label className="block text-[var(--c-ink-3)] mb-1">URL</label>
                    <div className="text-cyan-700 dark:text-cyan-300 truncate">{selectedBug.url}</div>
                  </div>
                )}
                {selectedBug.browser && (
                  <div>
                    <label className="block text-[var(--c-ink-3)] mb-1">Browser</label>
                    <div className="text-[var(--c-ink-2)] truncate">{selectedBug.browser}</div>
                  </div>
                )}
                {selectedBug.device && (
                  <div>
                    <label className="block text-[var(--c-ink-3)] mb-1">Device</label>
                    <div className="text-[var(--c-ink-2)]">{selectedBug.device}</div>
                  </div>
                )}
              </div>

              {/* Screenshot - Enhanced Thumbnail Version */}
              {screenshotUrl && (
                <div>
                  <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-2">
                    Screenshot
                  </label>
                  <div className="relative group w-48 h-auto"> {/* Added fixed width container */}
                  <img
                    src={screenshotUrl}
                    alt="Bug screenshot"
                    className="rounded-lg border border-[var(--c-line)] w-full h-auto cursor-pointer hover:border-red-500/50 transition-all shadow-lg"
                    onClick={() => window.open(screenshotUrl, '_blank')}
                  />
                    {/* Overlay hint on hover */}
                    <div 
                      className="absolute inset-0 bg-[var(--c-surface)] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none rounded-lg"
                    >
                      <span className="text-[10px] font-bold text-[var(--c-ink)] uppercase tracking-widest">
                        Click to expand ↗
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Comments */}
              <div>
                <label className="block text-xs font-bold text-[var(--c-ink-2)] uppercase mb-3">Comments</label>
                <div className="space-y-3 mb-4">
                  {selectedBug.comments && selectedBug.comments.length > 0 ? (
                    selectedBug.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className={`rounded-lg p-3 ${
                          comment.is_internal
                            ? "border border-purple-500/30 bg-purple-500/10"
                            : "border border-[var(--c-line)] bg-[var(--c-sunken)]"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-[var(--c-ink)]">{comment.author_username}</span>
                          <div className="flex items-center gap-2">
                            {comment.is_internal && (
                              <span className="px-2 py-0.5 rounded bg-purple-500/20 text-[10px] font-bold text-purple-700 dark:text-purple-300">
                                INTERNAL
                              </span>
                            )}
                            <span className="text-[10px] text-[var(--c-ink-3)]">
                              {new Date(comment.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-[var(--c-ink-2)] whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-[var(--c-ink-3)] text-sm">No comments yet</div>
                  )}
                </div>

                {/* Add Comment */}
                {!selectedBug.is_archived && (
                  <div className="space-y-3">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] px-4 py-3 text-sm text-[var(--c-ink)] placeholder-white/30 outline-none focus:border-red-500/50 resize-none"
                      rows={3}
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-[var(--c-ink-2)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                          className="rounded"
                        />
                        Internal note (admins only)
                      </label>
                      <button
                        onClick={addComment}
                        disabled={!newComment.trim()}
                        className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-xs font-bold text-red-800 dark:text-red-200 hover:bg-red-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Add Comment
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              {!selectedBug.is_archived && (
                <div className="pt-4 border-t border-[var(--c-line)]">
                  <button
                    onClick={() => archiveBug(selectedBug.id)}
                    className="px-4 py-2 rounded-lg bg-gray-500/20 border border-gray-500/40 text-xs font-bold text-[var(--c-ink)] hover:bg-gray-500/30 transition-colors"
                  >
                    Archive Bug
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-[var(--c-ink-3)]">
              Select a bug to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
